<template>
  <div class="rd-screen wh-req">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <ui-segment v-model="stockType" :options="modes" :label="$t('warehouse_shell.mode')" />
      <div class="ui-chips">
        <ui-toggle :pressed="showStock" @click="showStock = !showStock">
          <v-icon size="16">mdi-warehouse</v-icon>
          {{ $t("warehouse.show_warehouse_status") }}
        </ui-toggle>
        <ui-toggle :pressed="showAllGroceries" @click="showAllGroceries = !showAllGroceries">
          <v-icon size="16">mdi-food-apple-outline</v-icon>
          {{ $t("warehouse.show_all_groceries") }}
        </ui-toggle>
      </div>

      <template #right>
        <v-menu v-if="stockType === 'ordered'" offset-y left content-class="rd-vmenu">
          <template #activator="{ on, attrs }">
            <button
              type="button"
              class="ui-btn ui-btn--ok ui-btn--sm"
              data-action="order"
              :disabled="!selectedItems.length"
              :title="selectedItems.length ? '' : $t('no_items_selected_for_order')"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon size="16" color="#ffffff">mdi-cart-outline</v-icon>
              {{ $t("order") }}<span v-if="selectedItems.length" class="rd-num"> ({{ selectedItems.length }})</span>
              <v-icon size="16" color="#ffffff">mdi-chevron-down</v-icon>
            </button>
          </template>
          <ul class="rd-vmenu__list" role="menu">
            <li>
              <button type="button" role="menuitem" data-action="request" @click="orderGroceries(false)">
                <v-icon size="16">mdi-file-document-outline</v-icon>
                {{ $t("warehouse.request_quote") }}
              </button>
            </li>
            <li>
              <button type="button" role="menuitem" data-action="purchase" @click="orderGroceries(true)">
                <v-icon size="16">mdi-cart-arrow-down</v-icon>
                {{ $t("warehouse.purchase_order") }}
              </button>
            </li>
          </ul>
        </v-menu>
        <button
          v-else-if="stockType === 'exit'"
          type="button"
          class="ui-btn ui-btn--primary ui-btn--sm"
          data-action="release"
          :disabled="!selectedItems.length"
          :title="selectedItems.length ? '' : $t('no_items_selected_for_waybill')"
          @click="openWayBill()"
        >
          <v-icon size="16" color="#ffffff">mdi-truck-delivery-outline</v-icon>
          {{ $t("warehouse.release") }}<span v-if="selectedItems.length" class="rd-num"> ({{ selectedItems.length }})</span>
        </button>
      </template>
    </report-tools>

    <p v-if="showStockCombinedHint" class="wh-note">
      <v-icon size="16">mdi-information-outline</v-icon>
      {{ $t("warehouse.stock_combined_hint") }}
    </p>

    <report-state v-if="busy" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-clipboard-list-outline" :title="emptyTitle" :text="emptyText"></report-state>

    <ui-table
      v-else
      variant="report"
      table-class="wh-tbl"
      :columns="columns"
      :rows="rows"
      :sort="sort"
      :selected="(row) => row.picked"
      @sort="sortBy"
    >
      <template #head-shift="{ column }">
        <report-shift-tag :shift="column.shift"></report-shift-tag>
      </template>

      <template #cell-image="{ row }">
        <span class="wh-thumb">
          <img v-if="row.image" :src="row.image" alt="" loading="lazy" />
          <v-icon v-else size="16">mdi-food-apple-outline</v-icon>
        </span>
      </template>

      <template #cell-quantity="{ row }">
        <b>{{ qty(row.quantity) }}</b>
        <span class="wh-unit">{{ row.measure }}</span>
      </template>

      <template #cell-stock="{ row }">
        <span class="ui-chip ui-chip--pill wh-pill" :class="row.short ? 'ui-chip--warn' : 'ui-chip--ok'" :title="row.short ? $t('warehouse_shell.stock_low') : $t('warehouse_shell.stock_ok')">
          <v-icon size="14">{{ row.short ? "mdi-alert-outline" : "mdi-check" }}</v-icon>
          {{ qty(row.stock) }}
        </span>
        <span class="wh-unit">{{ row.measure }}</span>
      </template>

      <template #cell-order="{ row }">
        <input
          class="wh-qty"
          type="number"
          min="0"
          step="0.001"
          inputmode="decimal"
          :max="stockType === 'exit' ? row.stock : undefined"
          :value="row.order == null ? '' : row.order"
          :aria-label="`${$t('warehouse.quantity_for_action', { action: actionVerb })}: ${row.name}`"
          @input="updateItemOrder(row.item, $event.target.value)"
          @keydown.enter.prevent="focusNext($event)"
        />
      </template>

      <template #cell-ordered="{ row }">
        {{ qty(row.ordered) }}
        <span class="wh-unit">{{ row.measure }}</span>
      </template>

      <template #cell-after="{ row }">
        <span v-if="row.picked" class="ui-chip ui-chip--pill wh-pill" :class="row.afterLow ? 'ui-chip--warn' : 'ui-chip--ok'">
          <v-icon size="14">{{ row.afterLow ? "mdi-alert-outline" : "mdi-check" }}</v-icon>
          {{ qty(row.after) }}
        </span>
        <span v-else class="rd-muted">{{ qty(row.after) }}</span>
        <span class="wh-unit">{{ row.measure }}</span>
      </template>
    </ui-table>

    <warehouse-purchase-order-dialog v-model="purchaseOrderDialog" :edited-item="editedItem" @success="load" />
    <warehouse-waybill-dialog v-model="waybillDialog" :edited-item="editedItem" />
  </div>
</template>

<script>
import WarehouseTabRequirement from "@/components/warehouse/tabs/WarehouseTabRequirement.vue";
import { SHIFT_KEYS, DEFAULT_SHIFTS } from "@/constants/warehouse";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";

/**
 * Trebovanje na računaru: koje namirnice (po smenama) trebaju za naručene obroke, sa stanjem u magacinu i
 * unosom količina za naručivanje ili izdavanje. Nasleđuje učitavanje, izračun, štampu, Excel i dijaloge iz
 * starog taba; mešaju se samo prikaz i radnje. Magacini, period, smene, prikaz i pretragu čita iz trake filtera.
 */
export default {
  name: "WarehouseDesktopRequirement",
  extends: WarehouseTabRequirement,
  mixins: [hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  data: () => ({
    sort: { key: "name", dir: 1 },
    // količine za namirnice koje nisu u izveštaju (Prikaži sve namirnice) nemaju gde da se upišu u učitane podatke
    extraOrders: {},
    // upisane količine po režimu: ono što je upisano za naručivanje ne sme da postane količina za izdavanje
    savedOrders: {},
    // stari tab učitava tek 200 ms posle otvaranja: do prvog učitavanja se prikazuje skelet, ne "nema trebovanja"
    hasLoaded: false,
  }),
  computed: {
    /** Kolone tabele: zavise od prikaza smena, stanja magacina i režima (potrebno / naruči / izdaj). */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "image", label: this.$t("image"), cls: "wh-col-img" },
        { key: "code", label: this.$t("code"), num: true, muted: true, sortable: true, value: (row) => row.code || "—" },
        { key: "name", label: this.$t("title"), name: true, sortable: true },
        ...this.shiftColumns.map((shift) => ({ key: `s${shift}`, slot: "shift", shift, align: "end", num: true, zero: true, sortable: true, format: (value) => this.qty(value) })),
        { key: "quantity", label: this.$t("warehouse.quantity_based_on_ordered_meals"), headCls: "wh-th-wrap", align: "end", num: true, sortable: true },
        ...(this.showStock ? [{ key: "stock", label: this.$t("warehouse.warehouse_status"), align: "end", num: true, sortable: true }] : []),
        ...(this.actionMode
          ? [
              { key: "order", label: this.$t("warehouse.quantity_for_action", { action: this.actionVerb }), headCls: "wh-th-wrap", align: "end" },
              { key: "ordered", label: this.$t(this.stockType === "exit" ? "warehouse.exit" : "warehouse.ordered"), align: "end", num: true, zero: true, sortable: true },
              { key: "after", label: this.$t(this.stockType === "exit" ? "warehouse.quantity_after_issue" : "warehouse.quantity_after_order"), headCls: "wh-th-wrap", align: "end", num: true, sortable: true },
            ]
          : []),
      ];
    },
    busy() {
      return this.loading || !this.hasLoaded;
    },
    /** Brojke u zaglavlju ljuske: nema ih dok se podaci učitavaju. */
    shownKpis() {
      return this.busy ? [] : this.kpis;
    },
    modes() {
      return [
        { value: "required", label: this.$t("warehouse.required") },
        { value: "ordered", label: this.$t("warehouse.order") },
        { value: "exit", label: this.$t("warehouse.issue") },
      ];
    },
    actionMode() {
      return this.stockType !== "required";
    },
    actionVerb() {
      return this.$t(this.stockType === "exit" ? "issuing" : "ordering");
    },
    /** Kolone smena: samo u prikazu "Jela iz grupa", za izabrane smene. */
    shiftColumns() {
      if (this.filters.showType === "daily") {
        return [];
      }
      return (this.filters.selectedShifts || DEFAULT_SHIFTS).filter((shift) => shift >= 1 && shift <= 4).sort();
    },
    /** Stari izračun, sa količinama upisanim uz namirnice koje nisu u izveštaju. */
    translatedInventory() {
      const items = WarehouseTabRequirement.computed.translatedInventory.call(this);
      return items.map((item) =>
        item.id in this.extraOrders ? { ...item, order: this.extraOrders[item.id] } : item
      );
    },
    searchedItems() {
      return this.customSearch(this.translatedInventory, this.headers, this.filters.search);
    },
    rows() {
      const rows = this.searchedItems.map((item) => {
        const quantity = Number(item.quantity) || 0;
        const stock = parseFloat(item.stock || 0);
        const order = item.order == null ? null : Number(item.order);
        const picked = Number(order) > 0;
        const after = parseFloat(this.calculateAfterActionQuantity(item)) || 0;
        const row = {
          key: item.id,
          item,
          code: item.code || "",
          name: item.name,
          measure: item.measure || "",
          image: item.images && item.images.length ? this.groceryImageUrl(item.images[0]) : "",
          quantity,
          stock,
          order,
          ordered: Number(item.ordered) || 0,
          after,
          picked,
          short: stock < quantity,
          afterLow: after < quantity,
        };
        SHIFT_KEYS.forEach((key, index) => {
          row[`s${index + 1}`] = parseFloat(item.shift?.[key] || 0);
        });
        return row;
      });
      return this.sortRows(rows);
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("no_requirements");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("no_requirements_yet");
    },
    kpis() {
      const kpis = [{ key: "items", label: this.$t("warehouse_shell.kpi_items"), value: this.rows.length, tone: "inverse" }];
      if (this.actionMode) {
        kpis.push({
          key: "picked",
          label: this.$t("warehouse_shell.kpi_picked"),
          value: this.selectedItems.length,
          of: this.rows.length,
          tone: "accent",
        });
      }
      if (this.showStock) {
        kpis.push({
          key: "short",
          label: this.$t("warehouse_shell.kpi_short"),
          value: this.rows.filter((row) => row.short).length,
        });
      }
      return kpis;
    },
  },
  watch: {
    shownKpis: {
      handler(kpis) {
        this.$emit("kpis", kpis);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
        if (!loading && this.loadCount > 0) {
          this.hasLoaded = true;
        }
      },
      immediate: true,
    },
    // stari tab je pratio samo početni datum i nije ponovo učitavao kad se promeni prikaz jela
    "filters.dates.to"() {
      this.debouncedLoad();
    },
    "filters.showType"() {
      this.debouncedLoad();
    },
    stockType(mode, previous) {
      this.$set(this.savedOrders, previous, this.currentOrders());
      this.inventory.forEach((item) => this.$set(item, "order", null));
      this.extraOrders = {};
      Object.entries(this.savedOrders[mode] || {}).forEach(([id, value]) => this.setOrder(id, value));
    },
  },
  methods: {
    /** Upisane količine trenutnog režima: { id namirnice: količina }. */
    currentOrders() {
      const orders = {};
      this.inventory.forEach((item) => {
        if (item.order != null) {
          orders[item.id] = item.order;
        }
      });
      Object.entries(this.extraOrders).forEach(([id, value]) => {
        if (value != null) {
          orders[id] = value;
        }
      });
      return orders;
    },
    qty(value) {
      return this.formatQuantity(value);
    },
    updateItemOrder(item, value) {
      this.setOrder(item.id, this.normalizeOrderValue(value));
    },
    setOrder(id, value) {
      const source = this.inventory.find((inventoryItem) => String(inventoryItem.id) === String(id));
      if (source) {
        this.$set(source, "order", value);
      } else {
        this.$set(this.extraOrders, id, value);
      }
    },
    /** Enter u polju količine prelazi na polje u sledećem redu (brz unos niz listu). */
    focusNext(event) {
      const inputs = [...this.$el.querySelectorAll(".wh-qty")];
      const next = inputs[inputs.indexOf(event.target) + 1];
      if (next) {
        next.focus();
        next.select();
      }
    },
    async copyTable() {
      const headers = [
        this.$t("code"),
        this.$t("title"),
        ...this.shiftColumns.map((shift) => this.$t("report_mobile.shift_n", { n: shift })),
        this.$t("warehouse.quantity_based_on_ordered_meals"),
        this.$t("unit_of_measurment"),
        ...(this.showStock ? [this.$t("warehouse.warehouse_status")] : []),
        ...(this.actionMode ? [this.$t("warehouse.quantity_for_action", { action: this.actionVerb })] : []),
      ];
      const rows = this.rows.map((row) => [
        row.code,
        row.name,
        ...this.shiftColumns.map((shift) => this.qty(row[`s${shift}`])),
        this.qty(row.quantity),
        row.measure,
        ...(this.showStock ? [this.qty(row.stock)] : []),
        ...(this.actionMode ? [row.order == null ? "" : row.order] : []),
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
